import { useMemo } from "react"
import { ColorValue } from "sharedTypes/badge"
import { useBadgeTemplate } from "./useBadgeTemplate"
import { getCapability } from "../utils/badgeHelpers"
import { MediaColorMode } from "../utils/printers"

// Color palettes for different printer capabilities
const COLOR_PALETTES = {
  "black-only": {
    colors: [
      "#000000", // Pure black
      "#ffffff", // Pure white
    ],
    description: "Black and white only",
    helpText: "Your printer only supports pure black and white colors.",
  },
  "black-red": {
    colors: [
      "#000000", // Pure black
      "#ffffff", // Pure white
      "#ff0000", // Pure red
    ],
    description: "Black, white, and red only",
    helpText: "Your printer only supports pure black, white, and red colors.",
  },
  "full-color": {
    colors: [
      // Use the existing color scheme plus more comprehensive colors
      "#000000", "#163160", "#4B72D3", "#417505", "#D0021B", "#DE9215", "#4A4A4A",
      "#FFFFFF", "#EDEDED", "#FFFDE5", "#F6FCFF", "#FFF5F5", "#F7F6F7", "#FBF6F1",
      // Additional full spectrum colors
      "#FF0000", "#00FF00", "#0000FF", "#FFFF00", "#FF00FF", "#00FFFF",
      "#800080", "#008000", "#000080", "#800000", "#808000", "#008080",
    ],
    description: "Full color spectrum",
    helpText: "Your printer supports the full color spectrum.",
  },
} as const

export type ColorConstraints = {
  allowedColors: readonly ColorValue[]
  colorMode: MediaColorMode
  description: string
  helpText: string
  isColorAllowed: (color: string) => boolean
}

/**
 * Hook that provides color constraints based on the current template's
 * printer and label capabilities
 */
export const usePrinterColorConstraints = (): ColorConstraints => {
  const { currentTemplate } = useBadgeTemplate()

  return useMemo(() => {
    if (!currentTemplate) {
      // Fallback to full color if no template is available
      const palette = COLOR_PALETTES["full-color"]

      return {
        allowedColors: [...palette.colors],
        colorMode: "full-color",
        description: palette.description,
        helpText: palette.helpText,
        isColorAllowed: () => true,
      }
    }

    // Get the color mode from the template's printer + label combination using capabilities matrix
    const { colorMode } = getCapability(currentTemplate.printerModel, currentTemplate.printerMedia)

    const palette = COLOR_PALETTES[colorMode]

    // Function to check if a color is allowed
    const isColorAllowed = (color: string): boolean => {
      if (!color) return true // Empty colors are allowed

      const normalizedColor = color.toLowerCase().startsWith("#") ? color.toLowerCase() : `#${color.toLowerCase()}`

      if (colorMode === "full-color") {
        return true // Any color is allowed for full-color printers
      }

      // For black-only and black-red printers, only exact palette colors are allowed
      return palette.colors.some(
        (allowedColor) => allowedColor.toLowerCase() === normalizedColor,
      )
    }

    return {
      allowedColors: [...palette.colors],
      colorMode,
      description: palette.description,
      helpText: palette.helpText,
      isColorAllowed,
    }
  }, [currentTemplate])
}

/**
 * Hook that provides filtered colors based on printer constraints
 * Useful for color picker components that need to show only valid colors
 */
export const useConstrainedColorPalette = (baseColors?: string[]): string[] => {
  const { allowedColors, isColorAllowed } = usePrinterColorConstraints()

  return useMemo(() => {
    if (!baseColors) {
      return [...allowedColors]
    }

    // Filter the base colors to only include allowed ones
    return baseColors.filter(isColorAllowed)
  }, [baseColors, allowedColors, isColorAllowed])
}

/** The printer's colors as a color picker's swatches; custom colors only on a full-color label. */
export const usePrinterSwatches = () => {
  const { allowedColors, colorMode } = usePrinterColorConstraints()

  return { swatches: allowedColors, allowCustom: colorMode === "full-color" }
}
